import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import { client } from '../../api/client';
import { setCardNumber } from '../../api/mobile';
import { logEvent, EVENTS } from '../../lib/event-helper';

const parseJson = (raw) => {
    try {
        return JSON.parse(raw) || {};
    } catch (err) {
        return {};
    }
};
 
const devicesAdapter = createEntityAdapter({
    selectId: device => device.id
});
const initialState = {
    status: 'idle',
    error: null,
    cardData: parseJson(localStorage.getItem('card_data')),
    trialStatus: localStorage.getItem('trialStatus') || 'idle',
    trialExpires: localStorage.getItem('trialExpires'),
    loggedIn: localStorage.getItem('loggedIn') === 'true' ? true : false,
    udid: {
        status: 'idle',
        error: null
    },
    email: {
        status: 'idle',
        error: null,
    },
    userData: {
        status: 'idle',
        error: null,
        data: {
        }
    },
    devices: devicesAdapter.getInitialState({
        status: 'idle',
        error: null
    }),
    turningDevice: {
        status: 'idle',
        error: null
    },
    deletingDevice: {
        status: 'idle',
        error: null
    },
    iOSUser: {
        status: 'idle',
        error: null,
        data: {}
    },
    iOSUserPassword: {
        status: 'idle',
        error: null,
        value: ''
    }
};


export const addNewTrial = createAsyncThunk(
        'user/addNewTrial',
        async ({email, udid}) => {
    const response = await client.get(`/free_trial_ask_code/${email}/${udid}`);
    return response;
}
);

export const verifyPassword = createAsyncThunk(
        'user/verifyPassword',
        async ({code, udid}) => {
    const response = await client.get(`/free_trial_start/${code}/${udid}`);
    return response;
}
);

export const login = createAsyncThunk(
        'user/login',
        async ({udid, cardNumber, password}) => {
    const response = await client.post(`/check_card_login/${udid}/${cardNumber}`, {pwd: password});
    return response;
}
);

export const checkUdid = createAsyncThunk('user/checkUdid', async (udid) => {
    const response = await client.get('/checkUdidFT/' + udid);
    return response;
});

export const checkEmail = createAsyncThunk('user/checkEmail', async (email) => {
    const response = await client.get('/checkEmailFT/' + email);
    return response;
});

export const fetchUserData = createAsyncThunk('user/fetchUserData', async ({cardNumber, password}) => {
    const response = await client.post(`/get_card_info/${cardNumber}/${encodeURIComponent(password)}`, {});
    return response;
});


export const fetchDevices = createAsyncThunk('user/fetchDevices', async (cardNumber) => {
    const response = await client.get(`/get_profiles/${cardNumber}`);
    return response;
});

export const turnDevice = createAsyncThunk('user/turnDevice', async ({profileId, status}) => {
    const response = await client.get(`/turn/${profileId}/${status ? 1 : 0}`);
    return response;
});

export const deleteDevice = createAsyncThunk('user/deleteDevice', async (profileId) => {
    const response = await client.get(`/delProfile/${profileId}`);
    return response;
});

export const createiOSUser = createAsyncThunk('user/createiOSUser', async ({nome, cognome, email}) => {
    const response = await client.post('/create_user_ios', {nome, cognome, email});
    return response;
});

export const getiOSUserPassword = createAsyncThunk('user/getiOSUserPassword', async({user_id, email}) => {
    const response = await client.get(
            `/get_pwd_register_user_ios/${user_id}/${email}`, 
             {responseType: 'text'}
            );
    return response;
});


const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        loggedOut(state, action) {
            state.loggedIn = false;
            state.trialStatus = 'idle';
        }
    },
    extraReducers: {
        [addNewTrial.pending]: (state, action) => {
            state.status = 'loading';
        },
        [addNewTrial.fulfilled]: (state, action) => {
            if (action.payload.status == 1) {
                state.status = 'succeeded';
                state.cardData.email = action.meta.arg.email;
                localStorage.setItem('card_data', JSON.stringify(state.cardData));
            } else {
                state.status = 'failed';
                state.error = action.payload.msg;
            }
        },
        [addNewTrial.rejected]: (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        },
        [verifyPassword.pending]: (state, action) => {
            state.status = 'loading';
        },
        [verifyPassword.fulfilled]: (state, action) => {
            if (action.payload.status == 1) {
                state.status = 'succeeded';
                state.trialStatus = 'started';
                state.loggedIn = true;
                setCardNumber(action.payload.cardid);
                localStorage.setItem('loggedIn', state.loggedIn);
                localStorage.setItem('trialStatus', state.trialStatus);
            } else {
                state.status = 'failed';
                state.error = action.payload.msg;
            }

        },
        [verifyPassword.rejected]: (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        },
        [login.pending]: (state, action) => {
            state.status = 'loading';
        },
        [login.fulfilled]: (state, action) => {
            if (action.payload.trial && action.payload.trial.exists == 1) {
                state.status = 'succeeded';
                state.loggedIn = true;
                let trial = action.payload.trial;
                state.cardData = {
                    ...trial,
                    email: state.cardData.email
                };
                
                if (state.cardData.expired == 1) {
                    state.trialStatus = 'expired';
                }
                setCardNumber(action.meta.arg.cardNumber, action.meta.arg.password);
            } else if (action.payload.pay_user && action.payload.pay_user.status == 1) {
                state.status = 'succeeded';
                state.loggedIn = true;
                state.cardData = {...action.payload.pay_user, inapp_user: action.payload.inapp_user};
                setCardNumber(action.meta.arg.cardNumber, action.meta.arg.password);
                state.trialStatus = 'idle';
            } else {
                state.status = 'failed';
                state.loggedIn = false;
                state.error = action.payload.msg;
            }
            localStorage.setItem('loggedIn', state.loggedIn);
            localStorage.setItem('card_data', JSON.stringify(state.cardData));
            localStorage.setItem('trialStatus', state.trialStatus);
        },
        [login.rejected]: (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        },
        [checkUdid.pending]: (state, action) => {
            state.udid.status = 'loading';
        },
        [checkUdid.fulfilled]: (state, action) => {
            if (action.payload.status == 1) {
                state.udid.status = 'succeeded';
            } else {
                state.udid.status = 'failed';
                state.udid.error = action.payload.msg;
            }
        },
        [checkUdid.rejected]: (state, action) => {
            state.udid.status = 'failed';
            state.udid.error = action.payload;
        },
        [checkEmail.pending]: (state, action) => {
            state.email.status = 'loading';
        },
        [checkEmail.fulfilled]: (state, action) => {
            if (action.payload.status == 1) {
                state.email.status = 'succeeded';
            } else {
                state.email.status = 'failed';
                state.email.error = action.payload.msg;
            }
        },
        [checkEmail.rejected]: (state, action) => {
            state.email.status = 'failed';
            state.email.error = action.payload;
        },
        [fetchUserData.pending]: (state, action) => {
            state.userData.status = 'loading';
        },
        [fetchUserData.fulfilled]: (state, action) => {
            if (action.payload.user) {
                state.userData.status = 'succeeded';
                state.userData.data = action.payload;
            } else {
                state.userData.status = 'failed';
            }
        },
        [fetchUserData.rejected]: (state, action) => {
            state.userData.status = 'failed';
            state.userData.error = action.payload;
        },
        [fetchDevices.pending]: (state, action) => {
            state.devices.status = 'loading';
        },
        [fetchDevices.fulfilled]: (state, action) => {
            state.devices.status = 'succeeded';

            devicesAdapter.setAll(state.devices, action.payload);
        },
        [fetchDevices.rejected]: (state, action) => {
            state.devices.status = 'failed';
            state.devices.error = action.payload;
        },
        [turnDevice.pending]: (state, action) => {
            state.turningDevice.status = 'loading';
        },
        [turnDevice.fulfilled]: (state, action) => {
            state.turningDevice.status = 'succeeded';
            state.devices.entities[action.meta.arg.profileId].pr_attivo =
                    action.meta.arg.status ? 1 : 0;
        },
        [turnDevice.rejected]: (state, action) => {
            state.turningDevice.status = 'failed';
            state.turningDevice.error = action.payload;
        },
        [deleteDevice.pending]: (state, action) => {
            state.deletingDevice.status = 'loading';
        },
        [deleteDevice.fulfilled]: (state, action) => {
            state.deletingDevice.status = 'succeeded';
            delete state.devices.entities[action.meta.arg];
            state.devices.ids = state.devices.ids.filter(id => id != action.meta.arg);
        },
        [deleteDevice.rejected]: (state, action) => {
            state.deletingDevice.status = 'failed';
            state.deletingDevice.error = action.payload;
        },
        [createiOSUser.pending]: (state, action) => {
            state.iOSUser.status = 'loading';
        },
        [createiOSUser.fulfilled]: (state, action) => {
            state.iOSUser.status = 'succeeded';
            state.iOSUser.data = action.payload;
            state.trialStatus = 'idle';
        },
        [createiOSUser.rejected]: (state, action) => {
            state.iOSUser.status = 'failed';
            state.iOSUser.error = action.payload;
        },
        [getiOSUserPassword.pending]: (state, action) => {
            state.iOSUserPassword.status = 'loading';
        },
        [getiOSUserPassword.fulfilled]: (state, action) => {
            state.iOSUserPassword.status = 'succeeded';
            state.iOSUserPassword.value = action.payload;
        },
        [getiOSUserPassword.rejected]: (state, action) => {
            state.iOSUserPassword.status = 'failed';
            state.iOSUserPassword.error = action.payload;
        }
    }
});


export default userSlice.reducer;

export const isLoggedIn = (state) => state.user.loggedIn;
export const selectTrial = (state) => {
    return {status: state.user.trialStatus, expires: state.user.trialExpires};
};
export const selectUserData = (state) => state.user.userData.data;
export const {loggedOut} = userSlice.actions;

export const {
    selectAll: selectDevices,
    selectById: selectDeviceById,
    selectIds: selectDeviceIds
} = devicesAdapter.getSelectors(state => state.user.devices);


